import type { ShiftSet } from '@/types/template';

/** Bucket id for unrestricted open/automatic shifts (any eligible worker). */
export const ANY_STAFF_GROUP_ID = 'any';
/** Bucket id for `specific`-method shifts (a single named person). */
export const SPECIFIC_PEOPLE_ID = 'specific';

/** A staff-group section of the template preview: the shifts a group may work. */
export interface StaffGroupBucketView {
  id: string;
  name: string;
  shifts: ShiftSet[];
}

/**
 * The staff-group bucket(s) a shift belongs to, for the template preview's
 * "Staff group" view. A template carries no assignments, so we group by the
 * shift's **restriction** staff groups:
 * - `specific` → the "Specific people" bucket (a named person, no group);
 * - open/automatic restricted to staff groups → one bucket per group (the shift
 *   is eligible for, and so appears under, each);
 * - open/automatic with no group restriction → the "Any staff group" bucket.
 *
 * The `any`/`specific` names here are English fallbacks; the component resolves
 * their display labels through Lingui by id.
 */
export function staffGroupBucketsForShift(shift: ShiftSet): Array<{ id: string; name: string }> {
  const staffing = shift.staffing;
  if (staffing.method === 'specific') {
    return [{ id: SPECIFIC_PEOPLE_ID, name: 'Specific people' }];
  }
  const groups = staffing.filters?.staffGroups ?? [];
  if (groups.length === 0) {
    return [{ id: ANY_STAFF_GROUP_ID, name: 'Any staff group' }];
  }
  return groups.map((group) => ({ id: group.id, name: group.name }));
}

/**
 * Groups template shifts into staff-group buckets (see
 * {@link staffGroupBucketsForShift}). A shift restricted to several groups
 * appears in each. Ordering: named groups alphabetically, then "Any staff
 * group", then "Specific people".
 */
export function groupShiftsByStaffGroup(shifts: ShiftSet[]): StaffGroupBucketView[] {
  const byId = new Map<string, StaffGroupBucketView>();

  for (const shift of shifts) {
    for (const bucket of staffGroupBucketsForShift(shift)) {
      const entry = byId.get(bucket.id);
      if (entry) {
        entry.shifts.push(shift);
      } else {
        byId.set(bucket.id, { id: bucket.id, name: bucket.name, shifts: [shift] });
      }
    }
  }

  return [...byId.values()].sort(compareBuckets);
}

/** Sort rank: named groups (0) → "Any staff group" (1) → "Specific people" (2). */
function compareBuckets(a: StaffGroupBucketView, b: StaffGroupBucketView): number {
  const rank = (id: string) => (id === SPECIFIC_PEOPLE_ID ? 2 : id === ANY_STAFF_GROUP_ID ? 1 : 0);
  const byRank = rank(a.id) - rank(b.id);
  return byRank !== 0 ? byRank : a.name.localeCompare(b.name);
}
